import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import {
  Table2, Info, FileCode2, Network, Columns3, TerminalSquare, Wrench, Code2, GitBranchPlus,
  GitCompareArrows,
  BookOpen,
  X, XCircle, Copy, ArrowRight,
  SplitSquareHorizontal, SplitSquareVertical,
  Pin, PinOff, Tag,
} from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useAppStore } from '@/stores/app-store';
import { useDialogStore } from '@/stores/dialog-store';
import { ContextMenu, type ContextMenuItem } from '@/components/shared/ContextMenu';
import type { WorkareaTab, ConnectionConfig } from '@/types';

// ═══════════════════════════════════════════
// Tab group color palette
// ═══════════════════════════════════════════

const GROUP_COLORS: string[] = [
  '#4a7fff', // blue
  '#3dd68c', // green
  '#f0c556', // yellow
  '#b07fff', // purple
  '#e0955c', // orange
  '#4dd0c8', // teal
];

function getGroupColor(group: string): string {
  let hash = 0;
  for (let i = 0; i < group.length; i++) {
    hash = ((hash << 5) - hash) + group.charCodeAt(i);
  }
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
}

// ═══════════════════════════════════════════
// Tab icon and title helpers
// ═══════════════════════════════════════════

function tabIcon(tab: WorkareaTab) {
  switch (tab.type) {
    case 'table':
      return <Table2 size={14} className="text-accent-green flex-shrink-0" />;
    case 'info':
      return <Info size={14} className="text-accent-blue flex-shrink-0" />;
    case 'query':
      return <FileCode2 size={14} className="text-accent-purple flex-shrink-0" />;
    case 'tunnel':
      return <Network size={14} className="text-accent-teal flex-shrink-0" />;
    case 'structure':
      return <Columns3 size={14} className="text-accent-orange flex-shrink-0" />;
    case 'terminal':
      return <TerminalSquare size={14} className="text-accent-green flex-shrink-0" />;
    case 'designer':
      return <Wrench size={14} className="text-accent-yellow flex-shrink-0" />;
    case 'code-editor':
      return <Code2 size={14} className="text-accent-teal flex-shrink-0" />;
    case 'erd':
      return <GitBranchPlus size={14} className="text-accent-purple flex-shrink-0" />;
    case 'diff':
      return <GitCompareArrows size={14} className="text-accent-purple flex-shrink-0" />;
    case 'markdown':
      return <BookOpen size={14} className="text-accent-blue flex-shrink-0" />;
    case 'admin-shell':
      return <TerminalSquare size={14} className="text-accent-yellow flex-shrink-0" />;
  }
}

function tabTitle(tab: WorkareaTab): string {
  switch (tab.type) {
    case 'table':
      return tab.table;
    case 'info':
      return tab.objectName;
    case 'query':
      return tab.title;
    case 'tunnel':
      return tab.tunnelName;
    case 'structure':
      return `${tab.table} (DDL)`;
    case 'terminal':
      return tab.title;
    case 'designer':
      return tab.tableName ? `${tab.tableName} (Design)` : 'New Table';
    case 'code-editor':
      return `${tab.objectName} (${tab.objectType})`;
    case 'erd':
      return `ERD \u2014 ${tab.databaseName}`;
    case 'diff':
      return `Diff: ${tab.tableA} \u2194 ${tab.tableB}`;
    case 'markdown':
      return tab.title;
    case 'admin-shell':
      return tab.title;
  }
}

interface ContextMenuState {
  position: { x: number; y: number };
  tabId: string;
}

interface TabBarProps {
  paneId: string;
  tabs: WorkareaTab[];
  activeTabId: string | null;
}

export function TabBar({ paneId, tabs, activeTabId }: TabBarProps) {
  const switchTab = useWorkspaceStore((s) => s.switchTab);
  const closeTab = useWorkspaceStore((s) => s.closeTab);
  const openTab = useWorkspaceStore((s) => s.openTab);
  const splitPane = useWorkspaceStore((s) => s.splitPane);
  const moveTab = useWorkspaceStore((s) => s.moveTab);
  const setFocusedPane = useWorkspaceStore((s) => s.setFocusedPane);
  const tabPins = useAppStore((s) => s.tabPins);
  const tabGroups = useAppStore((s) => s.tabGroups);
  const togglePinTab = useAppStore((s) => s.togglePinTab);
  const setTabGroup = useAppStore((s) => s.setTabGroup);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Sort tabs: pinned first, then unpinned, preserving insertion order within each group
  const sortedTabs = useMemo(() => {
    const pinned = tabs.filter((t) => tabPins[t.id]);
    const unpinned = tabs.filter((t) => !tabPins[t.id]);
    return [...pinned, ...unpinned];
  }, [tabs, tabPins]);

  // Look up the color tag for each tab's connection so tabs scoped to a
  // colored connection carry a 2px top accent — lets users tell prod (red)
  // from staging (blue) tabs at a glance, even with identical titles. Reuses
  // the sidebar's `['connections']` query cache, so no extra network cost.
  const { data: connections = [] } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
    staleTime: 60_000,
  });
  const tabAccentColor = useCallback((tab: WorkareaTab): string | null => {
    const connId = (tab as { connectionId?: string }).connectionId;
    if (!connId) return null;
    return connections.find((c) => c.id === connId)?.colorTag ?? null;
  }, [connections]);

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (!activeTabId || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-tab-id="${activeTabId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [activeTabId]);

  const handleClose = useCallback(
    (e: React.MouseEvent, tabId: string) => {
      e.stopPropagation();
      if (tabPins[tabId]) return; // Don't close pinned tabs via X button
      closeTab(tabId, paneId);
    },
    [closeTab, paneId, tabPins],
  );

  const handleTabClick = useCallback(
    (tabId: string) => {
      setFocusedPane(paneId);
      switchTab(tabId);
    },
    [switchTab, setFocusedPane, paneId],
  );

  const handleContextMenu = useCallback((e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ position: { x: e.clientX, y: e.clientY }, tabId });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Drag and drop handlers.
  //
  // We handle two cases:
  //   1. Drop on a tab → insert before or after that tab, depending on
  //      whether the cursor was on its left or right half. Works for both
  //      same-pane reorder and cross-pane move to a specific slot.
  //   2. Drop on empty bar space → append to the end of this pane.
  //
  // `dragOverIndex` stores the insertion point during drag so we can draw a
  // vertical indicator line between tabs, giving the user feedback about
  // where the tab will land.
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent, tabId: string) => {
      e.dataTransfer.setData('text/plain', JSON.stringify({ tabId, paneId }));
      e.dataTransfer.effectAllowed = 'move';
    },
    [paneId],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOver(true);
    },
    [],
  );

  const handleTabDragOver = useCallback(
    (e: React.DragEvent, tabIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      // Insert before this tab if the cursor is on its left half, otherwise
      // after it. Gives users 1-tab-width of precision for the drop target.
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const isLeftHalf = e.clientX < rect.left + rect.width / 2;
      setDragOverIndex(isLeftHalf ? tabIndex : tabIndex + 1);
      setDragOver(true);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const targetIndex = dragOverIndex;
      setDragOver(false);
      setDragOverIndex(null);
      try {
        const data = JSON.parse(e.dataTransfer.getData('text/plain')) as {
          tabId: string;
          paneId: string;
        };
        if (data.paneId === paneId) {
          // Same-pane reorder. No-op if user dropped outside any tab, since
          // a bare-bar drop without an index just means "leave it alone".
          if (targetIndex !== null) {
            moveTab(data.tabId, data.paneId, paneId, targetIndex);
          }
        } else {
          // Cross-pane — append to end if no specific slot was hovered.
          moveTab(data.tabId, data.paneId, paneId, targetIndex ?? undefined);
        }
      } catch {
        // Ignore invalid drag data
      }
    },
    [paneId, moveTab, dragOverIndex],
  );

  const getContextMenuItems = useCallback(
    (tabId: string): ContextMenuItem[] => {
      const tabIndex = tabs.findIndex((t) => t.id === tabId);
      const tab = tabs[tabIndex];
      if (!tab) return [];

      const isPinned = !!tabPins[tabId];
      const currentGroup = tabGroups[tabId] ?? null;
      // Admin shell is a singleton, always-pinned tab. Disable pin/close
      // controls so an admin can't accidentally unpin or close it.
      const isAdminShell = tab.type === 'admin-shell';

      const items: ContextMenuItem[] = [
        {
          label: isPinned ? 'Unpin Tab' : 'Pin Tab',
          icon: isPinned ? PinOff : Pin,
          onClick: () => togglePinTab(tabId),
          disabled: isAdminShell,
        },
        {
          label: currentGroup ? `Group: ${currentGroup}` : 'Set Group...',
          icon: Tag,
          onClick: async () => {
            const name = await useDialogStore.getState().showPrompt({
              title: 'Set Tab Group',
              message: 'Enter group name (leave empty to remove):',
              defaultValue: currentGroup ?? '',
              placeholder: 'Group name',
            });
            if (name !== null) {
              setTabGroup(tabId, name.trim() || null);
            }
          },
        },
        { label: '', separator: true },
        {
          label: 'Close',
          icon: X,
          onClick: () => closeTab(tabId, paneId),
          disabled: isPinned || isAdminShell,
        },
        {
          label: 'Close Others',
          icon: XCircle,
          onClick: () => {
            tabs.forEach((t) => {
              if (t.id !== tabId && !tabPins[t.id]) closeTab(t.id, paneId);
            });
          },
          disabled: tabs.filter((t) => t.id !== tabId && !tabPins[t.id]).length === 0,
        },
        {
          label: 'Close All Unpinned',
          icon: XCircle,
          onClick: () => {
            tabs.forEach((t) => {
              if (!tabPins[t.id]) closeTab(t.id, paneId);
            });
          },
          disabled: tabs.filter((t) => !tabPins[t.id]).length === 0,
        },
        {
          label: 'Close to the Right',
          icon: ArrowRight,
          onClick: () => {
            tabs.slice(tabIndex + 1).forEach((t) => {
              if (!tabPins[t.id]) closeTab(t.id, paneId);
            });
          },
          disabled: tabIndex >= tabs.length - 1,
        },
        { label: '', separator: true },
        {
          label: 'Split Right',
          icon: SplitSquareHorizontal,
          onClick: () => splitPane(paneId, 'horizontal', tabId),
        },
        {
          label: 'Split Down',
          icon: SplitSquareVertical,
          onClick: () => splitPane(paneId, 'vertical', tabId),
        },
        { label: '', separator: true },
        {
          label: 'Duplicate Tab',
          icon: Copy,
          onClick: () => {
            const dupeId = `${tab.id}:dupe:${Date.now()}`;
            openTab({ ...tab, id: dupeId } as WorkareaTab, paneId);
          },
          // Admin shell is a singleton — duplicating it would conflict
          // on the fixed `id: 'admin-shell'` and confuse pinning state.
          disabled: isAdminShell,
        },
      ];

      return items;
    },
    [tabs, tabPins, tabGroups, closeTab, openTab, splitPane, togglePinTab, setTabGroup, paneId],
  );

  return (
    <>
      <div
        ref={scrollRef}
        className={`flex items-end bg-dbgo-tab-bar border-b overflow-x-auto flex-shrink-0 ${
          dragOver ? 'ring-1 ring-inset ring-accent-blue/50' : ''
        }`}
        style={{ minHeight: 36 }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {sortedTabs.map((tab, tabIndex) => {
          const isActive = tab.id === activeTabId;
          const isPinned = !!tabPins[tab.id];
          const group = tabGroups[tab.id];
          const groupColor = group ? getGroupColor(group) : null;
          const accent = tabAccentColor(tab);
          const showInsertBefore = dragOverIndex === tabIndex;
          const showInsertAfter = dragOverIndex === tabIndex + 1 && tabIndex === sortedTabs.length - 1;

          return (
            <button
              key={tab.id}
              data-tab-id={tab.id}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id)}
              onDragOver={(e) => handleTabDragOver(e, tabIndex)}
              onClick={() => handleTabClick(tab.id)}
              onContextMenu={(e) => handleContextMenu(e, tab.id)}
              title={group ? `Group: ${group}` : undefined}
              className={`group relative flex items-center gap-1.5 h-[36px] px-3.5 text-xs font-ui whitespace-nowrap select-none transition-colors ${
                isActive
                  ? 'bg-dbgo-tab-active text-text-primary'
                  : 'bg-dbgo-tab-bar text-text-secondary hover:bg-dbgo-tab-hover hover:text-text-primary'
              } ${isPinned ? 'border-l-2' : ''}`}
              style={{
                borderLeftColor: isPinned ? 'var(--dbgo-accent-blue, #4a7fff)' : undefined,
                // 2px top stripe for tabs rooted in a colored connection.
                // `inset box-shadow` so it doesn't nudge the content down.
                boxShadow: accent ? `inset 0 2px 0 0 ${accent}` : undefined,
              }}
            >
              {/* Drop indicators — a 2px vertical bar at the left edge of the
                  hovered tab (or the right edge if it's the last tab and the
                  cursor is past it) showing exactly where the dragged tab
                  will land on release. */}
              {showInsertBefore && (
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-accent-blue pointer-events-none" />
              )}
              {showInsertAfter && (
                <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-accent-blue pointer-events-none" />
              )}
              {/* Active indicator */}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent-blue" />
              )}
              {/* Group color dot */}
              {groupColor && (
                <span
                  className="w-[6px] h-[6px] rounded-full flex-shrink-0"
                  style={{ backgroundColor: groupColor }}
                />
              )}
              {/* Pin icon for pinned tabs */}
              {isPinned && (
                <Pin size={10} className="text-text-muted flex-shrink-0" />
              )}
              {tabIcon(tab)}
              <span className="max-w-[140px] truncate">{tabTitle(tab)}</span>
              {/* Close button: hidden for pinned tabs */}
              {!isPinned && (
                <span
                  onClick={(e) => handleClose(e, tab.id)}
                  className="w-[18px] h-[18px] flex items-center justify-center rounded-sm opacity-0 group-hover:opacity-50 hover:!opacity-100 hover:bg-dbgo-tab-hover transition-opacity ml-1"
                >
                  <X size={12} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {contextMenu && (
        <ContextMenu
          items={getContextMenuItems(contextMenu.tabId)}
          position={contextMenu.position}
          onClose={handleCloseContextMenu}
        />
      )}
    </>
  );
}
